import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, type DashboardData, type UserProfile } from '../lib/api';

export function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState<'economy' | 'balanced' | 'performance'>('balanced');
  const [selectedTemplate, setSelectedTemplate] = useState<'clean' | 'dev' | 'ops'>('dev');

  useEffect(() => {
    Promise.all([api.getDashboard(), api.getMe()])
      .then(([dashData, profileData]) => {
        setData(dashData);
        setProfile(profileData);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="auth-container">
        <div className="deploy-spinner" />
      </div>
    );
  }

  const modelEstimates = {
    economy: '~$52/mo',
    balanced: '~$85/mo',
    performance: '~$147/mo'
  };

  // Compute derived values from real data
  const totalRequests = data?.totalRequests ?? 0;
  const totalCostUsd = data?.totalCostUsd ?? 0;
  const totalSavedUsd = data?.totalSavedUsd ?? 0;
  const cacheHitRate = data?.cacheHitRate ?? 0;
  const budgetLimit = data?.budgetLimitUsd ?? 100;
  const budgetPct = budgetLimit > 0 ? Math.min(100, Math.round((totalCostUsd / budgetLimit) * 100)) : 0;
  const budgetRemaining = Math.max(0, budgetLimit - totalCostUsd);
  const budgetOffset = 339.3 - (339.3 * budgetPct / 100);

  const mix = data?.modelMix ?? { haiku: 0, sonnet: 0, opus: 0 };
  const mixTotal = mix.haiku + mix.sonnet + mix.opus || 1;
  const haikuPct = Math.round((mix.haiku / mixTotal) * 100);
  const sonnetPct = Math.round((mix.sonnet / mixTotal) * 100);
  const opusPct = 100 - haikuPct - sonnetPct;

  // Daily trend bars (proportional heights)
  const trend = data?.dailyTrend ?? [];
  const maxTrendCost = Math.max(...trend.map(d => d.cost), 1);

  const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : n.toLocaleString();
  const fmtUsd = (n: number) => `$${n.toFixed(2)}`;

  return (
    <div className="dashboard">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="logo">
          <span className="logo-icon">🦞</span>
          <div>
            <span className="logo-text">CLAWZEMPIC</span>
            <div className="logo-sub">The Inference Diet</div>
          </div>
        </div>

        <div className="soul-badge">
          <span className="soul-badge-icon">⚡</span>
          <div className="soul-badge-info">
            <span className="soul-badge-name">10x Engineer</span>
            <span className="soul-badge-sub">3 skills active</span>
          </div>
        </div>

        <nav className="nav">
          <Link to="/dashboard" className="nav-item active">
            <span className="nav-icon">◆</span>
            <span>Overview</span>
          </Link>
          <a href="#" className="nav-item">
            <span className="nav-icon">💬</span>
            <span>Chat</span>
          </a>
          <a href="#" className="nav-item">
            <span className="nav-icon">⚡</span>
            <span>Integrations</span>
          </a>
          <a href="#" className="nav-item">
            <span className="nav-icon">📚</span>
            <span>Library</span>
          </a>
          <a href="#" className="nav-item">
            <span className="nav-icon">⚙</span>
            <span>Settings</span>
          </a>
        </nav>

        <div className="plan-badge">
          <div className="plan-name">PRO PLAN</div>
          <div className="plan-usage">{fmt(totalRequests)} requests</div>
          <div className="usage-bar">
            <div className="usage-bar-fill requests" style={{ width: `${budgetPct}%` }}></div>
          </div>
          <div className="plan-inference">{fmtUsd(totalCostUsd)} / {fmtUsd(budgetLimit)} inference</div>
          <div className="usage-bar">
            <div className="usage-bar-fill inference" style={{ width: `${budgetPct}%` }}></div>
          </div>
        </div>

        <div className="sidebar-footer">
          <a href="#">Privacy</a><span className="footer-sep">&middot;</span><a href="#">Terms</a><span className="footer-sep">&middot;</span><a href="#">Docs</a>
          <br />&copy; 2026 Clawzempic
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <div className="page-header">
          <h1 className="page-title">Overview</h1>
          <p className="page-subtitle">Your inference health at a glance</p>
        </div>

        {/* Hero Savings Card */}
        <div className="hero-savings">
          <div className="hero-savings-content">
            <div className="hero-savings-label">TOTAL SAVED THIS MONTH</div>
            <div className="hero-savings-value">{fmtUsd(totalSavedUsd)}</div>
            <div className="hero-savings-subtitle">Across routing, caching, compression, and scripts</div>

            <div className="hero-savings-meta">
              <div className="hero-savings-meta-item">
                <span className="value">--</span> vs last month
              </div>
              <div className="hero-savings-meta-item">
                <span className="divider">|</span>
              </div>
              <div className="hero-savings-meta-item">
                <span className="value">{totalSavedUsd + totalCostUsd > 0 ? Math.round((totalSavedUsd / (totalSavedUsd + totalCostUsd)) * 100) : 0}%</span> savings rate
              </div>
              <div className="hero-savings-meta-item">
                <span className="divider">|</span>
              </div>
              <div className="hero-savings-meta-item">
                <span className="value">{fmt(totalRequests)}</span> requests
              </div>
            </div>
          </div>

          <div className="mascot-bubble">
            <div className="mascot-speech">Looking good! Your bill lost 12 more pounds this month.</div>
            <div className="mascot-img">🦞</div>
          </div>
        </div>

        {/* Comparison Row */}
        <div className="comparison-row">
          <div className="card">
            <div className="card-header">
              <span className="card-title">Your Spend</span>
              <span className="card-period">Feb 2026</span>
            </div>
            <div className="card-value green">{fmtUsd(totalCostUsd)}</div>
            <div className="card-breakdown">
              Inference: {fmtUsd(totalCostUsd)}
            </div>

            <div className="trend-bars">
              {trend.map((d, i) => (
                <div key={d.date} className={`trend-bar${i === trend.length - 1 ? ' current' : ''}`}
                     style={{ height: `${Math.max(5, (d.cost / maxTrendCost) * 100)}%` }}></div>
              ))}
            </div>
            <div className="trend-labels">
              {trend.map((d, i) => (
                <span key={d.date} className={`trend-label${i === trend.length - 1 ? ' current' : ''}`}>
                  {new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Inference Budget</span>
              <span className="card-period">Feb 2026</span>
            </div>

            <div className="budget-meter">
              <div className="budget-circle">
                <svg width="120" height="120">
                  <defs>
                    <linearGradient id="budgetGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#34D399', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#6ee7b7', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  <circle cx="60" cy="60" r="54" className="budget-circle-bg" />
                  <circle cx="60" cy="60" r="54" className="budget-circle-fill"
                          strokeDasharray="339.3" strokeDashoffset={budgetOffset} />
                </svg>
                <div className="budget-text">
                  <div className="budget-percentage">{budgetPct}%</div>
                  <div className="budget-label">used</div>
                </div>
              </div>
            </div>

            <div className="budget-status">
              <span className="amount">{fmtUsd(budgetRemaining)}</span> remaining
            </div>

            <div className="budget-note">
              <div className="indicator"></div>
              <span>Overage billing: OFF</span>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-card-label">Scripts Saved</div>
            <div className="stat-card-value">--</div>
            <div className="stat-card-meta">No data source yet</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Cache Hit Rate</div>
            <div className="stat-card-value">{cacheHitRate}%</div>
            <div className="stat-card-meta">{fmt(totalRequests)} requests</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Model Mix</div>
            <div className="stat-card-value">{haikuPct}% Haiku</div>
            <div className="stat-card-meta">{sonnetPct}% Sonnet · {opusPct}% Opus</div>
          </div>
        </div>

        {/* Model Preference */}
        <div className="model-preference">
          <div className="model-preference-title">Model Preference</div>

          <div className="model-options">
            <div
              className={`model-option ${selectedModel === 'economy' ? 'selected' : ''}`}
              onClick={() => setSelectedModel('economy')}
            >
              <div className="model-option-name">Economy</div>
              <div className="model-option-desc">Maximum Haiku. Lowest cost.</div>
            </div>
            <div
              className={`model-option ${selectedModel === 'balanced' ? 'selected' : ''}`}
              onClick={() => setSelectedModel('balanced')}
            >
              <div className="model-option-name">Balanced</div>
              <div className="model-option-desc">Smart routing. Best value.</div>
            </div>
            <div
              className={`model-option ${selectedModel === 'performance' ? 'selected' : ''}`}
              onClick={() => setSelectedModel('performance')}
            >
              <div className="model-option-name">Performance</div>
              <div className="model-option-desc">Maximum Opus. Highest quality.</div>
            </div>
          </div>

          <div className="model-estimate">
            <span className="model-estimate-label">Estimated cost at current usage</span>
            <span className="model-estimate-value">{modelEstimates[selectedModel]}</span>
          </div>

          <div className="model-note">Changes take effect immediately</div>
        </div>

        {/* Agent Configuration */}
        <div className="agent-config">
          <div className="agent-config-title">Agent Template</div>
          <div className="agent-config-subtitle">Your agent's personality and pre-loaded skills</div>

          <div className="template-options">
            <div
              className={`template-option ${selectedTemplate === 'clean' ? 'selected' : ''}`}
              onClick={() => setSelectedTemplate('clean')}
            >
              <div className="template-option-header">
                <span className="template-icon">🔧</span>
                <div className="template-option-name">Clean & Crisp</div>
              </div>
              <div className="template-option-desc">No opinions. Full control. Base capabilities only.</div>
              <div className="template-skills">0 skills pre-loaded</div>
            </div>
            <div
              className={`template-option ${selectedTemplate === 'dev' ? 'selected' : ''}`}
              onClick={() => setSelectedTemplate('dev')}
            >
              <div className="template-option-header">
                <span className="template-icon">⚡</span>
                <div className="template-option-name">10x Engineer</div>
              </div>
              <div className="template-option-desc">Code review, debugging, architecture advice.</div>
              <div className="template-skills">3 skills: Code Reviewer, Debug Assistant, Architecture Guide</div>
            </div>
            <div
              className={`template-option ${selectedTemplate === 'ops' ? 'selected' : ''}`}
              onClick={() => setSelectedTemplate('ops')}
            >
              <div className="template-option-header">
                <span className="template-icon">🚀</span>
                <div className="template-option-name">Ops Rockstar</div>
              </div>
              <div className="template-option-desc">Monitoring, deployment, incident response.</div>
              <div className="template-skills">3 skills: CI/CD Manager, Alert Handler, Runbook Writer</div>
            </div>
          </div>

          <div className="template-note">
            <span className="template-note-icon">💡</span>
            <span>Changing templates updates your agent's SOUL.md and active skills. Takes effect on next message.</span>
          </div>
        </div>
      </main>
    </div>
  );
}
